import { useLocation } from "react-router";

import { CFTC_RISK_STATEMENT_SHORT } from "@/lib/disclosures";
import { useTenant } from "@/hooks/useTenant";

/**
 * CFTC Rule 1.55(c)/(d) futures risk disclosure footer (SFWL-007).
 *
 * Renders below <Footer /> in AppShell on every route. A tenant's custom
 * `disclosureFooter` copy always wins (regardless of marketScope) — that
 * lets a tenant override or supplement the built-in statement. Absent a
 * custom footer, the condensed CFTC statement renders only for
 * `marketScope: 'futures'` tenants. Default Sovrun branding
 * (`marketScope: 'all'`, no custom footer) renders nothing, since Sovrun
 * itself is not a regulated futures venue.
 *
 * <Footer /> is `fixed bottom-0` on every route except "/" (see Footer.tsx),
 * so this in-flow content would otherwise sit permanently behind it. We
 * mirror PageContainer's own clearance pattern (`pb-28` below the fixed
 * bar, no clearance on the landing page where Footer isn't fixed) so the
 * disclosure text is actually visible at full scroll instead of merely
 * present in the DOM.
 */
export function DisclosureFooter() {
  const tenant = useTenant();
  const isLanding = useLocation().pathname === "/";

  const copy = tenant.disclosureFooter
    ? tenant.disclosureFooter
    : tenant.marketScope === "futures"
      ? CFTC_RISK_STATEMENT_SHORT
      : null;

  if (!copy) return null;

  return (
    <div
      className={
        isLanding
          ? "border-t border-slate-200 bg-white/60 px-4 py-3 text-center text-xs leading-relaxed text-slate-500"
          : "border-t border-slate-200 bg-white/60 px-4 pb-28 pt-3 text-center text-xs leading-relaxed text-slate-500"
      }
    >
      <p className="mx-auto max-w-6xl">{copy}</p>
    </div>
  );
}
